<template>
  <IdUpload
    v-if="step === 'uploadId'"
    @photoRead="onPhotoRead"
    @continueClick="continueClick"
    @deletePhoto="deletePhoto"
    :imageSrc="imagePhotoSrc"
    :showVerification="showVerification"
    :showConfirmation="showConfirmation"
    :inProgress="inProgress"
  />
  <SelfieUpload
    v-else
    @photoRead="onPhotoRead"
    @continueClick="continueClick"
    @deletePhoto="deletePhoto"
    :imageSrc="imagePhotoSrc"
    :showVerification="showVerification"
    :showConfirmation="showConfirmation"
    :inProgress="inProgress"
  />
</template>

<script lang="ts">
import Vue from "vue";
import { normalizeSizeAndOrientation } from "heliosx-common/dist/orientation";
import { readBlobAsDataUrl } from "heliosx-common/dist/readImage";

import IdUpload from "./IdUpload.vue";
import SelfieUpload from "./SelfieUpload.vue";
import axios, { AxiosResponse } from "axios";
import { amplitudeEvent } from "@/MXPixel/MedexpressAmplitude";
import { IdVerificationAttemptResponse, IdVerificationStep } from "@/IdVerification/types";

import "@stylesheets/common/tailwind-utilities.css";
import { appendQueryParameter } from "../Utils/UrlUtils";

const MAX_FILE_SIZE_BYTES = 100_000_000; // 100MB
const VALID_FILE_TYPES = ["image/jpeg", "image/png", "image/gif"];

interface IdVerificationData {
  image?: Blob;
  imagePhotoSrc?: string;
  showVerification: boolean;
  showConfirmation: boolean;
  step: IdVerificationStep;
  normalizedPhoto?: Blob;
  uploadInProgress: boolean;
  redirectInProgress: boolean;
  documentId?: number;
}

export default Vue.extend({
  props: {
    putIdUrl: {
      type: String,
      required: true,
    },
    putPhotoUrl: {
      type: String,
      required: true,
    },
    createAttemptUrl: {
      type: String,
      required: true,
    },
    nextUrl: {
      type: String,
      required: true,
    },
  },
  components: {
    IdUpload,
    SelfieUpload,
  },
  mounted() {
    amplitudeEvent("View ID Verification Screen", {
      kyc_provider: "heliosx",
    });
  },
  data(): IdVerificationData {
    return {
      image: undefined,
      imagePhotoSrc: undefined,
      showVerification: true,
      showConfirmation: false,
      step: "uploadId",
      normalizedPhoto: undefined,
      uploadInProgress: false,
      redirectInProgress: false,
      documentId: undefined,
    };
  },
  computed: {
    inProgress(): boolean {
      return this.uploadInProgress || this.redirectInProgress;
    },
  },
  methods: {
    onPhotoRead(blob: Blob) {
      if (blob?.size >= MAX_FILE_SIZE_BYTES) {
        amplitudeEvent("ID Verification Large File", {
          fileType: blob?.type,
          kyc_provider: "heliosx",
        });
      }

      this.image = blob;
      normalizeSizeAndOrientation(this.image, 1920)
        .then((result: Blob) => {
          this.normalizedPhoto = result;
          return readBlobAsDataUrl(result);
        })
        .then((result: string) => {
          this.imagePhotoSrc = result;
          this.showVerification = false;
          this.showConfirmation = true;
        })
        .catch((e: unknown) => {
          // `blob` is converted from PDF to jpeg in <SelectImage />
          if (!VALID_FILE_TYPES.includes(blob?.type)) {
            amplitudeEvent("ID Verification Incorrect File Type", {
              fileType: blob?.type,
              kyc_provider: "heliosx",
            });
            console.error(e);
          }
        });
    },
    deletePhoto() {
      this.imagePhotoSrc = undefined;
      this.showVerification = true;
      this.showConfirmation = false;
    },
    continueClick() {
      if (this.step === "uploadId") {
        return this.uploadId();
      } else {
        return this.uploadSelfie();
      }
    },
    redirect(location: string) {
      this.redirectInProgress = true;
      amplitudeEvent("Complete ID Verification", {
        kyc_provider: "heliosx",
      });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "complete_id_verification",
      });
      window.location.href = location;
    },
    uploadId() {
      return this.upload(this.putIdUrl).then((id: number) => {
        this.imagePhotoSrc = undefined;
        this.normalizedPhoto = undefined;
        this.showVerification = true;
        this.showConfirmation = false;
        this.step = "uploadSelfie";

        this.documentId = id;
        amplitudeEvent("Submit upload Id (front)", {
          kyc_provider: "heliosx",
        });
      });
    },
    getRedirectUrl(advancedOrderReferences?: string[]) {
      if (!advancedOrderReferences || advancedOrderReferences.length === 0) {
        return this.nextUrl;
      }
      return appendQueryParameter(this.nextUrl, "orderReferencesToTrack", advancedOrderReferences.join(","));
    },
    async uploadSelfie() {
      try {
        const id = await this.upload(this.putPhotoUrl);

        amplitudeEvent("Submit upload selfie", {
          kyc_provider: "heliosx",
        });

        const advancedOrderReferences = await axios
          .post<IdVerificationAttemptResponse, AxiosResponse<IdVerificationAttemptResponse>>(this.createAttemptUrl, {
            documentUploadId: this.documentId,
            selfieUploadId: id,
          })
          .then((response) => response.data?.advancedOrderReferences);
        this.redirect(this.getRedirectUrl(advancedOrderReferences));
      } catch (e) {
        console.error(`Failed ID verification request: ${e.message}`);
        throw e;
      }
    },

    async upload(target: string) {
      if (this.uploadInProgress) {
        return Promise.reject();
      }

      if (!this.normalizedPhoto) {
        return Promise.reject();
      }

      this.uploadInProgress = true;

      try {
        const response = await axios.put(target, this.normalizedPhoto, {
          headers: {
            "content-type": this.normalizedPhoto.type,
          },
        });

        const { id } = response.data;

        return id;
      } catch (e) {
        console.error(`Failed ID upload request: ${e.message}`);
        return Promise.reject("Failed to upload ID");
      } finally {
        this.uploadInProgress = false;
      }
    },
  },
});
</script>
